/**
 * Login Page — thin wrapper over @audra/marketing-ui's shared <LoginScreen>.
 *
 * Flow-specific bits the shared component doesn't know about:
 *
 *   1. Auto-redirect already-authenticated users to `/dashboard`.
 *
 *   2. Microsoft SSO callback (`?token=...`). The shared LoginScreen renders
 *      the form; flow's SSO flow returns to /login WITH a token in the URL
 *      and we exchange it for a session via `/api/auth` before redirecting.
 *      Keep this in the wrapper so the shared package stays generic.
 *
 *   3. Toast surface. Flow's `useToast()` is shared across the app — error
 *      / success toasts use the same Toaster mounted at the App root.
 *      Inline errors from the shared LoginScreen still render too; this is
 *      additive.
 *
 * The form chrome, validation, password-eye toggle, remember-me, Microsoft
 * button visual, and `wm-shell-bg` background all come from the shared
 * package.
 */
import { useEffect, useState } from "react"
import { useNavigate } from "react-router"
import { LoginScreen } from "@audra/marketing-ui"
import { useAuth } from "@/hooks/use-auth"
import { useToast } from "@/hooks/use-toast"

// audra-id Phase 2 Layer B. When VITE_AUDRA_ID_ENABLED is on, /login
// redirects to id.audra.digital/auth/entra/login — the cross-domain
// audra_session cookie set by audra-id flows back to flow's backend
// (which now accepts it via apps/web/backend/src/lib/audra-id.ts).
const AUDRA_ID_ENABLED =
  import.meta.env.VITE_AUDRA_ID_ENABLED === "true"
const AUDRA_ID_ORIGIN =
  import.meta.env.VITE_AUDRA_ID_ORIGIN ||
  "https://staging.id.windmill.ai"

export default function LoginPage() {
  const navigate = useNavigate()
  const { login, refreshToken, isLoading, isAuthenticated } = useAuth()
  const { toast } = useToast()
  const [ssoExchangeInProgress, setSsoExchangeInProgress] = useState(false)

  // audra-id flow: as soon as the page mounts, redirect to the SSO
  // endpoint with return_to set to the requested destination. A
  // <noscript> fallback link lets non-JS users bounce manually.
  useEffect(() => {
    if (!AUDRA_ID_ENABLED) return
    // If we're handling the legacy Microsoft SSO callback (?token=...),
    // let the existing exchange below run — don't bounce mid-flow.
    if (new URLSearchParams(window.location.search).get("token")) return
    // Already authed locally? Let the existing redirect-to-dashboard
    // useEffect handle it instead of bouncing through audra-id again.
    if (isAuthenticated && !isLoading) return
    const dest =
      sessionStorage.getItem("redirect_after_login") || "/dashboard"
    const returnTo = `${window.location.origin}${dest}`
    window.location.href =
      `${AUDRA_ID_ORIGIN}/auth/entra/login?return_to=${encodeURIComponent(returnTo)}`
  }, [isAuthenticated, isLoading])

  // Auto-redirect once the auth context confirms a logged-in user.
  useEffect(() => {
    if (isAuthenticated && !isLoading && !ssoExchangeInProgress) {
      navigate("/dashboard")
    }
  }, [isAuthenticated, isLoading, ssoExchangeInProgress, navigate])

  // Microsoft SSO callback: complete the session exchange, then redirect.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const token = params.get("token")
    if (!token) return

    let cancelled = false
    setSsoExchangeInProgress(true)
    ;(async () => {
      try {
        const response = await fetch("/api/auth", {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!response.ok) throw new Error("SSO token validation failed")

        const data = await response.json()
        localStorage.setItem("auth_token", token)
        localStorage.setItem("auth_user", JSON.stringify(data.user))
        await refreshToken()
        if (cancelled) return
        toast({
          title: `Welcome, ${data.user.name}!`,
          description: "Signed in with Microsoft successfully.",
          variant: "success",
        })
        window.history.replaceState({}, "", "/login")
        navigate("/dashboard")
      } catch (err) {
        if (cancelled) return
        console.error("Microsoft callback handling failed:", err)
        toast({
          title: "Microsoft Login Failed",
          description: "We could not validate your Microsoft sign-in. Please try again.",
          variant: "destructive",
        })
        window.history.replaceState({}, "", "/login")
      } finally {
        if (!cancelled) setSsoExchangeInProgress(false)
      }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // audra-id mode: render a minimal placeholder while the browser
  // bounces. The useEffect above triggers the redirect; this just
  // covers the sub-second window before navigation starts. The
  // <noscript> fallback link lets non-JS users continue manually.
  if (AUDRA_ID_ENABLED && !ssoExchangeInProgress) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 text-sm text-gray-600">
        Redirecting to audra-id…
        <noscript>
          {` — `}
          <a
            href={`${AUDRA_ID_ORIGIN}/auth/entra/login`}
            className="ml-1 text-brand-primary underline"
          >
            click here
          </a>
        </noscript>
      </div>
    )
  }

  return (
    <LoginScreen
      appId="flow"
      subLabel="Flow"
      defaultReturnTo="/dashboard"
      subHeading="Continue to your Flow workspace."
      onSubmit={async ({ email, password }) => {
        try {
          const user = await login(email, password)
          toast({
            title: `Welcome back, ${user.name}!`,
            description: "Redirecting to dashboard...",
            variant: "success",
          })
          return { ok: true as const }
        } catch (err) {
          const message = err instanceof Error
            ? err.message
            : "Invalid email or password. Please check your credentials."
          toast({
            title: "Login failed",
            description: "Invalid email or password. Please check your credentials.",
            variant: "destructive",
          })
          return { ok: false as const, error: message }
        }
      }}
      onMicrosoftSSO={() => {
        window.location.href = "/api/auth/sso/login"
      }}
      microsoftEnabled={true}
    />
  )
}
